import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  HelperText,
  HelperTextItem,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  TextInput,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { PencilAltIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useImpersonationTargets } from '../../../hooks/useImpersonationTargets';
import { useAdminSplaLicensePricing } from '../../../hooks/useAdminSplaCatalog';
import { DECIMAL_RE, formatUnitPrice } from '../../../utils/money';
import type { SplaLicense, SplaPriceOverride } from '../../../types/models/Spla';
import type {
  ApiResponseSplaDefaultPrice,
  ApiResponseSplaOverridePrice,
  ApiResponseSplaPriceCleared,
} from '../../../types/api/ApiResponseSpla';
import ClientTypeahead from '../ProductsAndPrices/ClientTypeahead';

interface OverrideMutationResult {
  ok: boolean;
  /** Backend field-level error (data.unit_price), surfaced inline; null for non-field failures. */
  fieldError: string | null;
}

interface SplaOverrideRowProps {
  override: SplaPriceOverride;
  onSave: (clientId: number, unitPrice: string) => Promise<OverrideMutationResult>;
  onRevert: (clientId: number) => Promise<OverrideMutationResult>;
}

// One per-client override: price editable in place, "Revert to default" clears the cell (DELETE,
// idempotent) so the client falls back to the platform default.
function SplaOverrideRow({ override, onSave, onRevert }: SplaOverrideRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isReverting, setIsReverting] = useState(false);

  const trimmed = draft.trim();
  const isDraftValid = DECIMAL_RE.test(trimmed);
  const helperError =
    serverError ??
    (trimmed !== '' && !isDraftValid ? 'Enter a non-negative amount with up to 4 decimal places.' : null);

  const clientLabel = override.client_name ?? `Client #${override.client_id}`;

  const save = async () => {
    if (!isDraftValid) {
      return;
    }
    if (trimmed === override.unit_price) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    setServerError(null);
    const result = await onSave(override.client_id, trimmed);
    setIsSaving(false);
    if (result.ok) {
      setIsEditing(false);
    } else {
      setServerError(result.fieldError);
    }
  };

  const revert = async () => {
    setIsReverting(true);
    await onRevert(override.client_id);
    setIsReverting(false);
  };

  return (
    <Tr>
      <Td dataLabel="Client">{clientLabel}</Td>
      <Td dataLabel="Sale price (excl. VAT)">
        {isEditing ? (
          <Flex spaceItems={{ default: 'spaceItemsSm' }} flexWrap={{ default: 'nowrap' }}>
            <FlexItem>
              <TextInput
                id={`spla-override-price-${override.client_id}`}
                type="text"
                inputMode="decimal"
                value={draft}
                onChange={(_event, value) => {
                  setDraft(value);
                  if (serverError) {
                    setServerError(null);
                  }
                }}
                validated={helperError ? 'error' : 'default'}
                isDisabled={isSaving}
                aria-label={`Sale price for ${clientLabel} in euros, excluding VAT`}
              />
              {helperError && (
                <HelperText>
                  <HelperTextItem variant="error">{helperError}</HelperTextItem>
                </HelperText>
              )}
            </FlexItem>
            <FlexItem>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void save()}
                isLoading={isSaving}
                isDisabled={isSaving || !isDraftValid}
              >
                Save
              </Button>
            </FlexItem>
            <FlexItem>
              <Button variant="link" size="sm" onClick={() => setIsEditing(false)} isDisabled={isSaving}>
                Cancel
              </Button>
            </FlexItem>
          </Flex>
        ) : (
          formatUnitPrice(override.unit_price)
        )}
      </Td>
      <Td dataLabel="Actions" modifier="fitContent">
        {!isEditing && (
          <Flex spaceItems={{ default: 'spaceItemsSm' }} flexWrap={{ default: 'nowrap' }}>
            <FlexItem>
              <Button
                variant="plain"
                icon={<PencilAltIcon />}
                aria-label={`Edit the sale price for ${clientLabel}`}
                onClick={() => {
                  setDraft(override.unit_price);
                  setServerError(null);
                  setIsEditing(true);
                }}
                isDisabled={isReverting}
              />
            </FlexItem>
            <FlexItem>
              <Button
                variant="link"
                isInline
                onClick={() => void revert()}
                isLoading={isReverting}
                isDisabled={isReverting}
              >
                Revert to default
              </Button>
            </FlexItem>
          </Flex>
        )}
      </Td>
    </Tr>
  );
}

interface SplaLicensePriceModalProps {
  license: SplaLicense;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * The customer SALE-price grid for one catalog license: the platform default plus per-client
 * overrides (override-then-default resolution). The DISTRIBUTOR (Econocom) price is shown alongside
 * for margin context — a staff-only figure the tenant never sees. All prices are decimal strings,
 * ≤ 4 decimals, sent verbatim. The ClientPricesModal precedent.
 */
function SplaLicensePriceModal({ license, isOpen, onClose }: SplaLicensePriceModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { pricing, error, refetch } = useAdminSplaLicensePricing(license.id, isOpen);
  const { targets, loading: targetsLoading, error: targetsError } = useImpersonationTargets();

  // Platform-default inline editor.
  const [isEditingDefault, setIsEditingDefault] = useState(false);
  const [defaultDraft, setDefaultDraft] = useState('');
  const [defaultServerError, setDefaultServerError] = useState<string | null>(null);
  const [isSavingDefault, setIsSavingDefault] = useState(false);

  // Add-override form.
  const [addClientId, setAddClientId] = useState<number | null>(null);
  const [addDraft, setAddDraft] = useState('');
  const [addServerError, setAddServerError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const overrides = pricing?.overrides ?? [];

  const defaultTrimmed = defaultDraft.trim();
  const isDefaultDraftValid = DECIMAL_RE.test(defaultTrimmed);
  const defaultHelperError =
    defaultServerError ??
    (defaultTrimmed !== '' && !isDefaultDraftValid
      ? 'Enter a non-negative amount with up to 4 decimal places.'
      : null);

  const addTrimmed = addDraft.trim();
  const isAddPriceValid = DECIMAL_RE.test(addTrimmed);
  const addHelperError =
    addServerError ??
    (addTrimmed !== '' && !isAddPriceValid ? 'Enter a non-negative amount with up to 4 decimal places.' : null);

  // The picker offers only clients without an override yet. Depends on the stable `pricing`
  // reference (not the `?? []` expression, a fresh array each render).
  const availableClients = useMemo(() => {
    const taken = new Set((pricing?.overrides ?? []).map((o) => o.client_id));
    return targets.filter((t) => !taken.has(t.id)).map((t) => ({ id: t.id, name: t.name }));
  }, [targets, pricing]);

  // PUT /admin/spla/licenses/{id}/price — upsert the platform default.
  const saveDefault = async () => {
    if (!isDefaultDraftValid) {
      return;
    }
    setIsSavingDefault(true);
    setDefaultServerError(null);
    try {
      const response = await apiFetch(`/admin/spla/licenses/${license.id}/price`, {
        method: 'PUT',
        body: JSON.stringify({ unit_price: defaultTrimmed }),
      });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseSplaDefaultPrice | null;
      if (body?.status === 'success' && body.data?.price) {
        addNotification({
          title: 'Default sale price saved',
          srTitle: 'Success alert',
          description: `${license.part_number} now sells at ${formatUnitPrice(body.data.price.default_unit_price)} (excl. VAT) by default.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        setIsEditingDefault(false);
        await refetch();
      } else {
        const fieldError = body?.data?.unit_price ?? null;
        setDefaultServerError(fieldError);
        addNotification({
          title: 'Could not save the default price',
          srTitle: 'Error alert',
          description: fieldError ?? body?.message ?? 'Could not save the default price. Please try again.',
          variant: 'danger',
        });
        setDrawerOpen(true);
      }
    } catch (err) {
      addNotification({
        title: 'Could not save the default price',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSavingDefault(false);
    }
  };

  // PUT /admin/spla/licenses/{id}/price/{clientId} — used for both add and edit-in-place.
  const putOverride = async (clientId: number, unitPrice: string): Promise<OverrideMutationResult> => {
    try {
      const response = await apiFetch(`/admin/spla/licenses/${license.id}/price/${clientId}`, {
        method: 'PUT',
        body: JSON.stringify({ unit_price: unitPrice }),
      });
      const body = (await response.json().catch(() => null)) as ApiResponseSplaOverridePrice | null;
      if (body?.status === 'success' && body.data?.price) {
        const clientName = body.data.price.client_name ?? `client #${clientId}`;
        addNotification({
          title: 'Client price saved',
          srTitle: 'Success alert',
          description: `${license.part_number} for ${clientName} is now ${formatUnitPrice(body.data.price.unit_price)} (excl. VAT).`,
          variant: 'success',
        });
        setDrawerOpen(true);
        await refetch();
        return { ok: true, fieldError: null };
      }
      const fieldError = body?.data?.unit_price ?? null;
      addNotification({
        title: 'Could not save the client price',
        srTitle: 'Error alert',
        description: fieldError ?? body?.message ?? 'Could not save the client price. Please try again.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError };
    } catch (err) {
      addNotification({
        title: 'Could not save the client price',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError: null };
    }
  };

  // DELETE /admin/spla/licenses/{id}/price/{clientId} — revert to the platform default.
  const revertOverride = async (clientId: number): Promise<OverrideMutationResult> => {
    try {
      const response = await apiFetch(`/admin/spla/licenses/${license.id}/price/${clientId}`, {
        method: 'DELETE',
      });
      const body = (await response.json().catch(() => null)) as ApiResponseSplaPriceCleared | null;
      if (body?.status === 'success' && body.data?.cleared) {
        addNotification({
          title: 'Client price removed',
          srTitle: 'Success alert',
          description: `The client-specific price for ${license.part_number} was removed; that client reverts to the platform default.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        await refetch();
        return { ok: true, fieldError: null };
      }
      addNotification({
        title: 'Could not remove the client price',
        srTitle: 'Error alert',
        description: body?.message ?? 'Could not remove the client price. Please try again.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError: null };
    } catch (err) {
      addNotification({
        title: 'Could not remove the client price',
        srTitle: 'Error alert',
        description: err instanceof Error ? err.message : 'An unexpected error occurred.',
        variant: 'danger',
      });
      setDrawerOpen(true);
      return { ok: false, fieldError: null };
    }
  };

  const handleAdd = async () => {
    if (addClientId === null || !isAddPriceValid) {
      return;
    }
    setIsAdding(true);
    setAddServerError(null);
    const result = await putOverride(addClientId, addTrimmed);
    setIsAdding(false);
    if (result.ok) {
      setAddClientId(null);
      setAddDraft('');
    } else {
      setAddServerError(result.fieldError);
    }
  };

  const renderBody = () => {
    // Show the error only when there's nothing to display; once pricing is loaded a transient
    // refetch failure keeps the (stale) grid rather than blanking it.
    if (error && !pricing) {
      return (
        <Alert variant="danger" isInline title="Could not load the sale prices">
          {error}
        </Alert>
      );
    }

    if (!pricing) {
      return (
        <Flex justifyContent={{ default: 'justifyContentCenter' }} style={{ padding: '2rem' }}>
          <FlexItem>
            <Spinner aria-label="Loading sale prices" />
          </FlexItem>
        </Flex>
      );
    }

    return (
      <>
        <DescriptionList isHorizontal isCompact className="pf-v6-u-mb-md">
          <DescriptionListGroup>
            <DescriptionListTerm>Part number</DescriptionListTerm>
            <DescriptionListDescription>
              {pricing.part_number} ({pricing.agreement_type}
              {pricing.agreement_type === 'Academic' ? ` · ${pricing.offering}` : ''})
              {pricing.archived && (
                <>
                  {' '}
                  <Label color="grey" isCompact>
                    Retired
                  </Label>
                </>
              )}
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Product family</DescriptionListTerm>
            <DescriptionListDescription>{pricing.product_family}</DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Distributor (Econocom) price</DescriptionListTerm>
            <DescriptionListDescription>
              {formatUnitPrice(pricing.distributor_unit_price)} (excl. VAT) —{' '}
              <Content component="small">staff-only figure, for margin context</Content>
            </DescriptionListDescription>
          </DescriptionListGroup>
          <DescriptionListGroup>
            <DescriptionListTerm>Platform default</DescriptionListTerm>
            <DescriptionListDescription>
              {isEditingDefault ? (
                <Flex spaceItems={{ default: 'spaceItemsSm' }} flexWrap={{ default: 'nowrap' }}>
                  <FlexItem>
                    <TextInput
                      id="spla-default-price-input"
                      type="text"
                      inputMode="decimal"
                      value={defaultDraft}
                      onChange={(_event, value) => {
                        setDefaultDraft(value);
                        if (defaultServerError) {
                          setDefaultServerError(null);
                        }
                      }}
                      validated={defaultHelperError ? 'error' : 'default'}
                      isDisabled={isSavingDefault}
                      aria-label="Default sale price in euros, excluding VAT"
                      placeholder="0.0000"
                    />
                    {defaultHelperError && (
                      <HelperText>
                        <HelperTextItem variant="error">{defaultHelperError}</HelperTextItem>
                      </HelperText>
                    )}
                  </FlexItem>
                  <FlexItem>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => void saveDefault()}
                      isLoading={isSavingDefault}
                      isDisabled={isSavingDefault || !isDefaultDraftValid}
                    >
                      Save
                    </Button>
                  </FlexItem>
                  <FlexItem>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={() => setIsEditingDefault(false)}
                      isDisabled={isSavingDefault}
                    >
                      Cancel
                    </Button>
                  </FlexItem>
                </Flex>
              ) : (
                <>
                  {pricing.default_unit_price !== null
                    ? `${formatUnitPrice(pricing.default_unit_price)} (excl. VAT)`
                    : 'Not priced yet'}{' '}
                  <Button
                    variant="link"
                    isInline
                    onClick={() => {
                      setDefaultDraft(pricing.default_unit_price ?? '');
                      setDefaultServerError(null);
                      setIsEditingDefault(true);
                    }}
                  >
                    {pricing.default_unit_price !== null ? 'Edit' : 'Set price'}
                  </Button>
                </>
              )}
            </DescriptionListDescription>
          </DescriptionListGroup>
        </DescriptionList>

        {pricing.default_unit_price === null && (
          <Alert variant="warning" isInline title="No default sale price yet" className="pf-v6-u-mb-md">
            Consumption of this license renders as &quot;Price on request&quot; on the customer page
            (<code>priced: false</code>) and invoicing will fail loud on it. Set a default — overrides
            only cover the clients listed below.
          </Alert>
        )}

        {overrides.length > 0 ? (
          <Table aria-label={`Client sale-price overrides for ${license.part_number}`} variant="compact">
            <Thead>
              <Tr>
                <Th>Client</Th>
                <Th>Sale price (excl. VAT)</Th>
                <Th screenReaderText="Row actions" />
              </Tr>
            </Thead>
            <Tbody>
              {overrides.map((o) => (
                <SplaOverrideRow key={o.client_id} override={o} onSave={putOverride} onRevert={revertOverride} />
              ))}
            </Tbody>
          </Table>
        ) : (
          <Content component="p">
            No client-specific sale prices yet. Every client pays the platform default; add an override below.
          </Content>
        )}

        <div className="pf-v6-u-mt-lg">
          <Content component="h3" className="pf-v6-u-mb-sm">
            Add a client price
          </Content>
          {targetsError && (
            <Alert variant="warning" isInline title="Could not load the client list" className="pf-v6-u-mb-sm">
              The list of clients could not be loaded, so a new override can&apos;t be added right now.
              Existing overrides above can still be edited or removed.
            </Alert>
          )}
          <Flex alignItems={{ default: 'alignItemsFlexStart' }} spaceItems={{ default: 'spaceItemsSm' }}>
            <FlexItem grow={{ default: 'grow' }}>
              <ClientTypeahead
                clients={availableClients}
                value={addClientId}
                onChange={setAddClientId}
                isDisabled={isAdding || targetsLoading}
                idPrefix="spla-price-override-client"
              />
            </FlexItem>
            <FlexItem>
              <TextInput
                id="spla-override-add-price"
                type="text"
                inputMode="decimal"
                value={addDraft}
                onChange={(_event, value) => {
                  setAddDraft(value);
                  if (addServerError) {
                    setAddServerError(null);
                  }
                }}
                validated={addHelperError ? 'error' : 'default'}
                isDisabled={isAdding}
                aria-label="New client sale price in euros, excluding VAT"
                placeholder="0.0000"
              />
            </FlexItem>
            <FlexItem>
              <Button
                variant="secondary"
                onClick={() => void handleAdd()}
                isLoading={isAdding}
                isDisabled={isAdding || addClientId === null || !isAddPriceValid}
              >
                Add
              </Button>
            </FlexItem>
          </Flex>
          {addHelperError && (
            <HelperText>
              <HelperTextItem variant="error">{addHelperError}</HelperTextItem>
            </HelperText>
          )}
        </div>
      </>
    );
  };

  return (
    <Modal variant="medium" isOpen={isOpen} onClose={onClose} aria-label={`Sale prices for ${license.part_number}`}>
      <ModalHeader
        title={`Sale prices — ${license.part_number}`}
        description="What customers pay per unit per month, EUR excl. VAT — the platform default, overridable per client."
      />
      <ModalBody tabIndex={0} aria-label={`Sale prices for ${license.part_number}`}>
        {renderBody()}
      </ModalBody>
      <ModalFooter>
        <Button variant="link" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default SplaLicensePriceModal;
